/**
 * Gliederung der Richtlinien-Werkstatt — Auswahl und Bearbeitung der Struktur im Baum selbst:
 * je Zeile ein Menü (danach einfügen, Unterabschnitt, entfallen lassen / wiederherstellen), das neue
 * Abschnitt wird an seiner künftigen Stelle direkt im Baum benannt, neue Abschnitte per Doppelklick
 * umbenannt. Alles bleibt Entwurf neben der geltenden Fassung.
 */

import { displayNumber } from "./guidelinesPath";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CornerDownRight, MoreHorizontal, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";

import { Badge } from "../components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { cn } from "../lib/utils";
import { useGuidelinesAPI, type GuidelineSection } from "./GuidelinesAPI";
import { retitle, subtreeEnd } from "./outline";

const indent = (level: number) => ({ paddingLeft: `${0.5 + (Math.max(1, level) - 1) * 0.9}rem` });

const errorText = (error: unknown) => (error instanceof Error ? error.message : String(error));

export function SectionTree(props: {
  customer: string;
  documentId: string;
  sections: GuidelineSection[];
  /** Gefiltert: Struktur bearbeiten nur in der vollständigen Gliederung. */
  filtered: boolean;
  selectedId: string;
  writable: boolean;
  openProposals: (sectionId: string) => number;
  onSelect: (number: string) => void;
  /** Nach einer Strukturänderung neu laden, ggf. diesen Abschnitt wählen. */
  onChanged: (selectNumber?: string) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(props.customer);
  const [insert, setInsert] = useState<{ anchor: GuidelineSection; position: "after" | "child" } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const editable = props.writable && !props.filtered;
  const inputRef = useRef<HTMLInputElement>(null);

  // Das Menü gibt den Fokus erst nach dem Schließen frei — danach ins Eingabefeld.
  useEffect(() => {
    if (!insert && !renaming) return;
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 50);
    return () => clearTimeout(timer);
  }, [insert, renaming]);

  const run = async (action: () => Promise<string | undefined>) => {
    setBusy(true);
    try {
      const select = await action();
      await props.onChanged(select);
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setBusy(false);
    }
  };

  const startInsert = (anchor: GuidelineSection, position: "after" | "child") => {
    setRenaming(null);
    setTitle("");
    setInsert({ anchor, position });
  };

  const commitInsert = () => {
    const pending = insert;
    if (!pending || !title.trim()) return setInsert(null);
    setInsert(null);
    run(async () => {
      const created = await api.createSection(props.documentId, pending.anchor._id, pending.position, title.trim());
      toast.success(t("guidelinesPage.sectionCreated", { number: created.number }));
      return created.number;
    });
  };

  const commitRename = (section: GuidelineSection) => {
    const value = title.trim();
    setRenaming(null);
    if (!value || value === section.title) return;
    run(async () => {
      await api.saveDraft(props.documentId, section._id, retitle(section.draft?.text ?? "", section.number, value), value);
      return section.number;
    });
  };

  const toggleRemoved = (section: GuidelineSection) =>
    run(async () => {
      const result = await api.setRemoved(props.documentId, section._id, !section.draft?.removed);
      toast.success(
        result.deleted.length
          ? t("guidelinesPage.sectionDeleted")
          : section.draft?.removed
            ? t("guidelinesPage.sectionRestored")
            : t("guidelinesPage.sectionRemoved"),
      );
      return result.deleted.length ? undefined : section.number;
    });

  const insertAt = insert ? subtreeEnd(props.sections, insert.anchor._id) : -1;
  const insertLevel = insert ? insert.anchor.level + (insert.position === "child" ? 1 : 0) : 1;

  const inputRow = (
    <div key="__insert" className="flex items-center gap-2 py-1 pr-2" style={indent(insertLevel)}>
      <CornerDownRight size={14} className="shrink-0 text-muted-foreground" />
      <input
        ref={inputRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commitInsert();
          if (e.key === "Escape") setInsert(null);
        }}
        onBlur={commitInsert}
        placeholder={insert?.position === "child" ? t("guidelinesPage.addChildHint", { number: insert.anchor.number }) : t("guidelinesPage.addAfterHint", { number: insert?.anchor.number })}
        aria-label={t("guidelinesPage.newTitle")}
        className="h-7 min-w-0 flex-1 rounded border border-primary bg-background px-2 text-sm outline-none"
      />
    </div>
  );

  const rows: JSX.Element[] = [];
  props.sections.forEach((s, i) => {
    if (i === insertAt) rows.push(inputRow);
    const open = props.openProposals(s._id);
    const removed = !!s.draft?.removed;
    rows.push(
      <div
        key={s._id}
        data-section-id={s._id}
        className={cn("group flex items-center hover:bg-accent", s._id === props.selectedId && "bg-accent font-medium")}
      >
        {renaming === s._id ? (
          <div className="flex min-w-0 flex-1 items-center gap-2 py-1 pr-2" style={indent(s.level)}>
            {s.number !== s.title ? <span className="shrink-0 tabular-nums text-muted-foreground">{displayNumber(s)}</span> : null}
            <input
              ref={inputRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename(s);
                if (e.key === "Escape") setRenaming(null);
              }}
              onBlur={() => commitRename(s)}
              aria-label={t("guidelinesPage.newTitle")}
              className="h-7 min-w-0 flex-1 rounded border border-primary bg-background px-2 text-sm outline-none"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => props.onSelect(s.number)}
            onDoubleClick={() => {
              if (!editable || !s.draft?.isNew) return;
              setInsert(null);
              setTitle(s.title);
              setRenaming(s._id);
            }}
            title={editable && s.draft?.isNew ? t("guidelinesPage.renameHint") : undefined}
            className="flex min-w-0 flex-1 items-baseline gap-2 py-1 pr-1 text-left text-sm"
            style={indent(s.level)}
          >
            {/* Abschnitte ohne Nummer („Inhalt“, „Einleitung“) tragen den Titel als Nummer — nicht doppelt zeigen. */}
            {s.number !== s.title ? <span className={cn("shrink-0 tabular-nums text-muted-foreground", removed && "line-through")}>{displayNumber(s)}</span> : null}
            <span className={cn("min-w-0 flex-1 truncate", removed && "text-muted-foreground line-through")}>{s.title}</span>
            {s.draft?.isNew ? <Badge variant="success" className="shrink-0 px-1.5 py-0 text-[10px]">{t("guidelinesPage.new")}</Badge> : null}
            {s.draft?.text != null && !s.draft.isNew ? (
              <Pencil size={12} className="shrink-0 text-muted-foreground" aria-label={t("guidelinesPage.draft")} />
            ) : null}
            {open ? <span className="shrink-0 rounded-full bg-warning px-1.5 text-[10px] text-warning-foreground">{open}</span> : null}
          </button>
        )}
        {editable && renaming !== s._id ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={busy}
                aria-label={t("guidelinesPage.structure")}
                className="mr-1 shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:bg-background focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
              >
                <MoreHorizontal size={14} />
              </button>
            </DropdownMenuTrigger>
            {/* Kein Fokus zurück auf den Menüknopf — sonst verlöre das neue Eingabefeld ihn sofort. */}
            <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
              <DropdownMenuItem onSelect={() => startInsert(s, "after")} disabled={removed}>
                <Plus size={14} className="mr-2" />
                {t("guidelinesPage.addAfter")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => startInsert(s, "child")} disabled={removed}>
                <CornerDownRight size={14} className="mr-2" />
                {t("guidelinesPage.addChild")}
              </DropdownMenuItem>
              {s.draft?.isNew ? (
                <DropdownMenuItem
                  onSelect={() => {
                    setInsert(null);
                    setTitle(s.title);
                    setRenaming(s._id);
                  }}
                >
                  <Pencil size={14} className="mr-2" />
                  {t("guidelinesPage.rename")}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => toggleRemoved(s)}>
                {removed ? <RotateCcw size={14} className="mr-2" /> : <Trash2 size={14} className="mr-2" />}
                {removed
                  ? t("guidelinesPage.restoreSection")
                  : s.draft?.isNew
                    ? t("guidelinesPage.deleteSection")
                    : t("guidelinesPage.removeSection")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>,
    );
  });
  if (insertAt === props.sections.length) rows.push(inputRow);

  return <>{rows}</>;
}
